import { useState, useEffect } from "react";
import { useGetFileVineContactsQuery } from "../../../redux/reduxQuery/fileVineApi";
import styled from "styled-components";
import { Theme } from "../../../components/atoms/theme";
import { Input, Select, Button } from "@tyrannosaurustech/ui";
import { toast } from "react-toastify";
import { SubtitlesM, ParagraphS } from "../../../components/atoms/Typography";

// Define the contact types based on FileVine's API
const CONTACT_TYPES = {
  ALL: "",
  PATIENT: "Patient",
  PROVIDER: "Medical Provider",
} as const;

// Custom debounce hook
const useDebounce = (value: string, delay: number) => {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(timer);
    };
  }, [value, delay]);

  return debouncedValue;
};

const Container = styled.div`
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 2rem;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 0.75rem;
  border: 1px solid ${Theme.colors.gray_100};
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const FilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FilterRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FilterColumn = styled.div`
  flex: 1;
  min-width: 300px;
`;

const TableContainer = styled.div`
  background: #fff;
  border-radius: 0.75rem;
  border: 1px solid ${Theme.colors.gray_100};
  overflow: hidden;
  position: relative;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHead = styled.thead`
  background: ${Theme.colors.neutrals_400};
`;

const TableRow = styled.tr`
  &:nth-child(even) {
    background: ${Theme.colors.neutrals_400};
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  border-bottom: 1px solid ${Theme.colors.gray_100};
`;

const TableHeader = styled.th`
  padding: 1rem;
  text-align: left;
  border-bottom: 1px solid ${Theme.colors.gray_100};
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
`;

const PageLengthContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PageLengthSelect = styled(Select)`
  width: 80px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 2rem;
  background: ${Theme.colors.neutrals_400};
  border-radius: 0.75rem;
  margin: 1rem 0;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid ${Theme.colors.gray_100};
  border-top: 4px solid ${Theme.colors.primaries_300};
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
`;

const FileVineContacts: React.FC = () => {
  const [page, setPage] = useState(1);
  const [personType, setPersonType] = useState<string>(CONTACT_TYPES.ALL);
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [pageSize, setPageSize] = useState<number>(10);
  const debouncedSearchQuery = useDebounce(searchQuery, 500);
  const offset = (page - 1) * pageSize;

  const queryParams = {
    personType: personType || undefined,
    limit: pageSize,
    offset,
    searchTerm: debouncedSearchQuery || undefined,
  };

  const { data, isLoading, isFetching, error } =
    useGetFileVineContactsQuery(queryParams);

  useEffect(() => {
    console.log("API Response:", data);
  }, [data]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePersonTypeChange = (value: string | number) => {
    setPersonType(value.toString());
    setPage(1);
  };

  const handlePageSizeChange = (value: string | number) => {
    setPageSize(Number(value));
    setPage(1);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(1);
  };

  const handleSearchSubmit = (event: React.FormEvent) => {
    event.preventDefault();
  };

  if (error) {
    toast.error("Error loading FileVine contacts");
  }

  const contacts = data?.items || [];
  const hasMore = data?.hasMore || false;

  return (
    <Container>
      <SubtitlesM>FileVine Contacts</SubtitlesM>

      <Card>
        <FilterContainer>
          <FilterRow>
            <FilterColumn>
              <Select
                label="Contact Type"
                value={
                  personType
                    ? {
                        id: personType,
                        label:
                          personType === CONTACT_TYPES.ALL
                            ? "All"
                            : personType === CONTACT_TYPES.PATIENT
                            ? "Patient"
                            : "Medical Provider",
                      }
                    : undefined
                }
                onChange={(value) => handlePersonTypeChange(value?.id)}
                options={[
                  { id: CONTACT_TYPES.ALL, label: "All" },
                  { id: CONTACT_TYPES.PATIENT, label: "Patient" },
                  { id: CONTACT_TYPES.PROVIDER, label: "Medical Provider" },
                ]}
              />
            </FilterColumn>
            <FilterColumn>
              <form onSubmit={handleSearchSubmit}>
                <Input
                  name="search"
                  label="Search Contacts"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder="Search by name, email, or phone"
                  iconLeft="MagnifyingGlassIcon"
                />
              </form>
            </FilterColumn>
          </FilterRow>
        </FilterContainer>
      </Card>

      {isLoading ? (
        <LoadingContainer>
          <LoadingSpinner />
        </LoadingContainer>
      ) : (
        <>
          <TableContainer>
            {isFetching && (
              <LoadingOverlay>
                <LoadingSpinner />
              </LoadingOverlay>
            )}
            <Table>
              <TableHead>
                <tr>
                  <TableHeader>ID</TableHeader>
                  <TableHeader>Type</TableHeader>
                  <TableHeader>Name</TableHeader>
                  <TableHeader>Email</TableHeader>
                  <TableHeader>Phone</TableHeader>
                  <TableHeader>Address</TableHeader>
                </tr>
              </TableHead>
              <tbody>
                {contacts.length > 0 ? (
                  contacts.map((contact: any) => (
                    <TableRow key={contact.personId.native}>
                      <TableCell>{contact.personId.native}</TableCell>
                      <TableCell>
                        {contact.personTypes?.join(", ") || "N/A"}
                      </TableCell>
                      <TableCell>{contact.fullName}</TableCell>
                      <TableCell>
                        {contact.emails?.[0]?.address || "N/A"}
                      </TableCell>
                      <TableCell>
                        {contact.phones
                          ?.map((phone: any) => phone.number)
                          .join(", ") || "N/A"}
                      </TableCell>
                      <TableCell>
                        {contact.addresses?.[0]?.fullAddress || "N/A"}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} style={{ textAlign: "center" }}>
                      No contacts found
                    </TableCell>
                  </TableRow>
                )}
              </tbody>
            </Table>
          </TableContainer>

          <PaginationContainer>
            <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
              <ParagraphS color={Theme.colors.gray_000}>
                Page {page}
                {hasMore && (
                  <span style={{ fontWeight: "bold" }}>
                    {" "}
                    (more pages available)
                  </span>
                )}
              </ParagraphS>
              <PageLengthContainer>
                <ParagraphS color={Theme.colors.gray_000}>Show:</ParagraphS>
                <PageLengthSelect
                  value={{
                    id: pageSize.toString(),
                    label: pageSize.toString(),
                  }}
                  onChange={(value) => handlePageSizeChange(value?.id)}
                  options={[
                    { id: "10", label: "10" },
                    { id: "25", label: "25" },
                    { id: "50", label: "50" },
                    { id: "100", label: "100" },
                  ]}
                />
              </PageLengthContainer>
            </div>
            <div>
              <Button
                base="secondary"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                style={{ marginRight: "0.5rem" }}
              >
                Previous
              </Button>
              <Button
                base="secondary"
                onClick={() => handlePageChange(page + 1)}
                disabled={!hasMore}
              >
                Next
              </Button>
            </div>
          </PaginationContainer>
        </>
      )}
    </Container>
  );
};

export default FileVineContacts;
